/**
 * Componente ErrorMessage (Mensaje de Error)
 *
 * Muestra mensajes de error de forma consistente.
 * Incluye opción para reintentar la acción.
 * Hace scroll automático al mensaje cuando aparece.
 *
 * Uso:
 * <ErrorMessage
 *   message="Error al cargar datos"
 *   onRetry={handleRetry}
 * />
 */

import { useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { Icon } from '../ui/icons';

export interface ErrorMessageProps {
  message: string;
  onRetry?: () => void;
  variant?: 'default' | 'inline' | 'full';
  title?: string;
  scrollToError?: boolean;
}

export default function ErrorMessage({
  message,
  onRetry,
  variant = 'default',
  title = 'Error',
  scrollToError = true,
}: ErrorMessageProps) {
  const errorRef = useRef<HTMLDivElement>(null);
  const baseStyles = 'p-4 bg-red-50 border border-red-200 rounded-lg text-red-800';

  // Scroll al error cuando aparece
  useEffect(() => {
    if (scrollToError && message && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [message, scrollToError]);
  
  if (variant === 'inline') {
    return (
      <div ref={errorRef} className={`${baseStyles} text-sm`}>
        <p>{message}</p>
      </div>
    );
  }

  if (variant === 'full') {
    return (
      <div ref={errorRef} className="flex flex-col items-center justify-center p-12 text-center">
        <Icon name="alert" size={48} color="#dc2626" className="mb-4" />
        <h3 className="text-lg font-semibold text-red-700 mb-2">{title}</h3>
        <p className="text-sm text-red-600 mb-4">{message}</p>
        {onRetry && (
          <Button variant="primary" onClick={onRetry}>
            Reintentar
          </Button>
        )}
      </div>
    );
  }

  return (
    <div ref={errorRef} className={baseStyles} role="alert">
      <div className="flex items-start gap-2">
        <Icon name="alert" size={20} className="mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium">{title}</p>
          <p className="text-sm mt-1">{message}</p>
        </div>
      </div>
      {onRetry && (
        <div className="mt-4">
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Reintentar
          </Button>
        </div>
      )}
    </div>
  );
}
